import { type ReactNode } from 'react';
import { Plus, X } from 'lucide-react';
import { Button, DatePicker, Input } from '@roger/ui-components';
import { formatDateToISO, parseISOToLocalDate } from '../../../utils/dateUtils';
import type { OccurrenceSpanRow } from './types';

export function OccurrenceSpansField({
  rows,
  onChange,
  capNote,
  maxRows = 4,
  readOnly = false,
}: {
  rows: OccurrenceSpanRow[];
  onChange: (next: OccurrenceSpanRow[]) => void;
  capNote?: ReactNode;
  maxRows?: number;
  readOnly?: boolean;
}) {
  const update = (i: number, patch: Partial<OccurrenceSpanRow>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () =>
    onChange([...rows, { code: '', from_date: '', through_date: '' }]);

  return (
    <div className='flex flex-col gap-2'>
      {capNote}
      {/* Each row labels its own fields, so below md this duplicate header
          would only misalign with rows that wrap. */}
      <div className='hidden items-center gap-2 text-xs font-medium text-muted-foreground md:flex'>
        <span className='w-20'>Span Code</span>
        <span className='w-36'>From Date</span>
        <span className='w-36'>Through Date</span>
      </div>
      {rows.map((entry, index) => (
        // Wraps on a phone: code + two dates + remove is ~430px, wider than
        // a 390px dialog. md:flex-nowrap keeps the desktop row as it was.
        <div
          key={index}
          className='flex flex-wrap items-end gap-2 md:flex-nowrap'
        >
          <div className='flex flex-col gap-1'>
            <span className='text-xs text-muted-foreground'>Code</span>
            <Input
              inputSize='sm'
              className='w-20 font-mono'
              placeholder='Code'
              disabled={readOnly}
              value={entry.code}
              onChange={e => update(index, { code: e.target.value })}
            />
          </div>
          <div className='flex flex-col gap-1'>
            <span className='text-xs text-muted-foreground'>From (start)</span>
            <DatePicker
              datePickerSize='sm'
              typable
              className='w-36'
              disabled={readOnly}
              date={parseISOToLocalDate(entry.from_date)}
              onDateChange={date =>
                update(index, { from_date: date ? formatDateToISO(date) : '' })
              }
            />
          </div>
          <div className='flex flex-col gap-1'>
            <span className='text-xs text-muted-foreground'>Through (end)</span>
            <DatePicker
              datePickerSize='sm'
              typable
              className='w-36'
              disabled={readOnly}
              date={parseISOToLocalDate(entry.through_date)}
              onDateChange={date =>
                update(index, {
                  through_date: date ? formatDateToISO(date) : '',
                })
              }
            />
          </div>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            aria-label='Remove occurrence span'
            disabled={readOnly}
            onClick={() => remove(index)}
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      ))}
      <Button
        type='button'
        variant='outline'
        size='sm'
        className='self-start'
        disabled={readOnly || rows.length >= maxRows}
        onClick={add}
      >
        <Plus className='mr-1 h-3 w-3' />
        Add Occurrence Span
      </Button>
    </div>
  );
}
